import { useUserIdWithLoading } from '@dreamstack/auth'
import { useFormat, useLocale, useTranslation } from '@dreamstack/i18n'
import type { GetNewsArticlePreviewsQueryResult } from '@dreamstack/investors-graphql'
import { getOrCreateAnonymousId } from '@dreamstack/tracking/src/getOrCreateAnonymousId'
import { trpc } from '@dreamstack/trpc/client'
import Image from 'next/image'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import tw, { styled } from 'twin.macro'
import type { NewsType } from '../index'
import { BodyText, ContinueReadingLink, FadeIn, HeadingH3 } from '../index'
import {
  StyledNewsDate,
  StyledNewsTypeTranslated,
  StyledNewsTypeWrapper,
} from './StyledNewsComponents'
import { getNewsPath } from './getNewsPath'

const StyledNewsDateWithMargin = tw(StyledNewsDate)`mt-px-16`

const StyledWrapper = styled.div`
  mark {
    ${tw`bg-accentroAquaVeryLight-full`}
  }
`

export const NewsHubPreview: FunctionComponent<
  React.PropsWithChildren<{
    article: NonNullable<
      NonNullable<GetNewsArticlePreviewsQueryResult['data']>['newsArticles']
    >['items'][0]
  }>
> = ({ article }) => {
  const t = useTranslation()
  const formatDate = useFormat()
  const locale = useLocale()
  const { userId } = useUserIdWithLoading()
  const router = useRouter()
  const { mutateAsync: trackButtonClickServer } =
    trpc.anonymousTracking.trackEvent.useMutation()
  if (!article) return null
  const { slug, newsType, title, previewText, image, date } = article
  if (!slug || !newsType) {
    // should never happen
    return null
  }
  const href = getNewsPath({ locale, slug, newsType: newsType as NewsType })
  const formattedDate = formatDate(date, 'dd. MMMM yyyy')
  const translatedNewsType = t(`accentro:newsTypes.${newsType}`)
  const file = article.file

  const trackClickCallToActionButton = async () => {
    const anonymousUserId = getOrCreateAnonymousId()
    await trackButtonClickServer({
      eventName: 'buttonClick',
      userId,
      anonymousUserId,
      data: {
        buttonAction: 'Ratgeber für Immobilienerwerb',
        url: router.asPath,
        additionalInfo: {
          newsType,
          articleSlug: slug,
          articleTitle: title,
        },
      },
    })
  }

  return (
    <StyledWrapper>
      <FadeIn opacityOnly>
        {image && (
          <div tw="relative h-px-320">
            <Image
              tw="object-cover"
              src={image.url ?? ''}
              alt={image.description || 'Newsarticle image'}
              layout="fill"
            />
          </div>
        )}
        {newsType && (
          <StyledNewsTypeWrapper>
            <StyledNewsTypeTranslated>
              {translatedNewsType}
            </StyledNewsTypeTranslated>
          </StyledNewsTypeWrapper>
        )}

        {typeof title === 'string' ? (
          <HeadingH3
            tw="hyphens[auto]"
            dangerouslySetInnerHTML={{ __html: title }}
          />
        ) : (
          <HeadingH3 tw="hyphens[auto]">{title}</HeadingH3>
        )}
        {date && !article.hideDate && (
          <StyledNewsDateWithMargin>
            {`Berlin, ${formattedDate}`}
          </StyledNewsDateWithMargin>
        )}

        <BodyText tw="mt-px-32">{previewText}</BodyText>
        <ContinueReadingLink
          onClick={trackClickCallToActionButton}
          href={href}
          file={file}
        />
      </FadeIn>
    </StyledWrapper>
  )
}
